import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { WorkspaceProvider } from "@/context/WorkspaceContext";
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import AppShell from "@/components/AppShell";
import Dashboard from "@/pages/Dashboard";
import CampaignGenerator from "@/pages/CampaignGenerator";
import KeywordResearch from "@/pages/KeywordResearch";
import Activity from "@/pages/Activity";
import CalendarPage from "@/pages/CalendarPage";
import Settings from "@/pages/Settings";
import Billing from "@/pages/Billing";
import AnalyticsPage from "@/pages/AnalyticsPage";
import AdminPage from "@/pages/AdminPage";
import GrowthPage from "@/pages/GrowthPage";
import PaymentResult from "@/pages/PaymentResult";
import Terms from "@/pages/legal/Terms";
import Privacy from "@/pages/legal/Privacy";
import Refunds from "@/pages/legal/Refunds";
import Security from "@/pages/legal/Security";
import SeoLanding from "@/pages/SeoLanding";
import BlogIndex from "@/pages/BlogIndex";
import BlogPost from "@/pages/BlogPost";
import AffiliatePage from "@/pages/AffiliatePage";
import AffiliateLanding from "@/pages/AffiliateLanding";
import RefRedirect from "@/pages/RefRedirect";
import StartFunnel from "@/pages/StartFunnel";
import ClientPortfolio from "@/pages/ClientPortfolio";
import ClientReview from "@/pages/ClientReview";
import ContentHub from "@/pages/ContentHub";
import PublicBlog from "@/pages/PublicBlog";
import CookieConsent from "@/components/CookieConsent";
import { Loader2 } from "lucide-react";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return children;
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/es" element={<Landing />} />
      <Route path="/for/:slug" element={<SeoLanding pageType="for" />} />
      <Route path="/vs/:slug" element={<SeoLanding pageType="vs" />} />
      <Route path="/ai/:slug" element={<SeoLanding pageType="ai" />} />
      <Route path="/es/for/:slug" element={<SeoLanding pageType="for" />} />
      <Route path="/es/vs/:slug" element={<SeoLanding pageType="vs" />} />
      <Route path="/es/ai/:slug" element={<SeoLanding pageType="ai" />} />
      <Route path="/tools/ai-hook-generator" element={<Navigate to="/start" replace />} />
      <Route path="/es/tools/ai-hook-generator" element={<Navigate to="/start" replace />} />
      <Route path="/start" element={<StartFunnel />} />
      <Route path="/portfolio/:token" element={<ClientPortfolio />} />
      <Route path="/review/:token" element={<ClientReview />} />
      <Route path="/es/start" element={<StartFunnel />} />
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/b/:wsId" element={<PublicBlog />} />
      <Route path="/b/:wsId/:slug" element={<PublicBlog />} />
      <Route path="/affiliates" element={<AffiliateLanding />} />
      <Route path="/ref/:slug" element={<RefRedirect />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/refunds" element={<Refunds />} />
      <Route path="/security" element={<Security />} />
      <Route path="/payment/success" element={<PaymentResult />} />
      <Route path="/payment/cancel" element={<PaymentResult />} />
      <Route
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppShell />
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/studio" element={<Navigate to="/campaign" replace />} />
        <Route path="/composer" element={<Navigate to="/campaign" replace />} />
        <Route path="/campaign" element={<CampaignGenerator />} />
        <Route path="/keywords" element={<KeywordResearch />} />
        <Route path="/content" element={<ContentHub />} />
        <Route path="/activity" element={<Activity />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/affiliate" element={<AffiliatePage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/admin/growth" element={<GrowthPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <CookieConsent />
        <Toaster position="top-right" theme="dark" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
